import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';

import {
  bibShape,
  availabilityShape,
  branchShape,
  libraryShape,
  subscriptionShape,
  userIdentityShape
} from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';

import {
  mapSearchBibToDisplayBib,
  mapSearchBibAvailabilityToDisplayAvailability,
  mapDisplayBibToAnalyticsBibEntity
} from 'app/helpers/displayBib';
import BibsActions from 'app/actions/BibsActions';

import {
  eventNames,
  METADATA_CATEGORY_TYPE,
  PROPERTY_CONTENT_COLLECTION_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TITLE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';
import { selectBibEntities, selectAvailabilityEntities } from 'app/selectors/EntitiesSelector';
import {
  selectAvailabilityError,
  selectBibItemsForCurrentBib,
  selectHoldingsForCurrentBib,
  selectSubscriptionsForCurrentBib
} from 'app/selectors/bibs/AvailabilitySelector';
import { selectAllDigitalFormats } from 'app/selectors/bibs/DigitalFormatsSelector';
import { selectCurrentLibrary, selectActivePreferredBranches } from 'app/selectors/LibrarySelector';
import { selectCurrentAccount, selectCurrentUser } from 'app/selectors/AuthSelector';
import holdingShape from 'app/shapes/holdingShape';
import { trackAnalyticsEvent, trackHoldingsConsumeAccess, trackHoldingsView } from 'app/actions/AnalyticsGa4Actions';
import bibItemShape from 'app/shapes/bibItemShape';

import TransactPanel from './TransactPanel';

export class TransactPanelContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  handleClick({ title, uri }) {
    const bibId = this.props.bib.get('id');
    const metadata = {
      metadata_category_type: METADATA_CATEGORY_TYPE.HOLDINGS_ELECTRONIC_RESOURCE,
      metadata_value: uri
    };
    const ui = {
      ui_container_type: UI_CONTAINER_TYPE.SECTION,
      ui_container_title: UI_CONTAINER_TITLE.AVAILABILITY_DETAILS,
      ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
      ui_component_label: title
    };

    this.props.trackHoldingsConsumeAccess({ bibId, metadata, ui });
  }

  handleAvailabilityButtonClick() {
    const { intl } = this.props;
    const bibId = this.props.bib.get('id');
    const ui = {
      ui_container_type: UI_CONTAINER_TYPE.SECTION,
      ui_container_title: UI_CONTAINER_TITLE.AVAILABILITY_DETAILS,
      ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
      ui_component_label: intl.formatMessage({ id: 'availability_by_location' })
    };
    this.props.trackHoldingsView({ bibId, ui });
  }

  trackAddToListClick({ uiEntity }) {
    const {
      bib: searchBib,
      realtimeAvailability: availability,
      trackAnalyticsEvent: dispatchTrackAnalyticsEvent
    } = this.props;

    const bibEntity = mapDisplayBibToAnalyticsBibEntity(
      mapSearchBibToDisplayBib(searchBib.toJS()),
      mapSearchBibAvailabilityToDisplayAvailability(availability?.toJS())
    );

    dispatchTrackAnalyticsEvent({
      entities: {
        bib: bibEntity,
        ui: uiEntity
      },
      eventName: eventNames.CONTENT_COLLECTION_ADD,
      properties: {
        content_collection_type: PROPERTY_CONTENT_COLLECTION_TYPE.LIST_TITLE
      }
    });
  }

  trackAddToNewListClick({ uiEntity }) {
    const {
      bib: searchBib,
      realtimeAvailability: availability,
      trackAnalyticsEvent: dispatchTrackAnalyticsEvent
    } = this.props;

    const bibEntity = mapDisplayBibToAnalyticsBibEntity(
      mapSearchBibToDisplayBib(searchBib.toJS()),
      mapSearchBibAvailabilityToDisplayAvailability(availability?.toJS())
    );

    dispatchTrackAnalyticsEvent({
      entities: {
        bib: bibEntity,
        ui: uiEntity
      },
      eventName: eventNames.USER_CONTENT_INITIATE
    });
  }

  render() {
    return (
      <TransactPanel
        {...this.props}
        handleClick={this.handleClick}
        handleAvailabilityButtonClick={this.handleAvailabilityButtonClick}
        trackAddToListClick={this.trackAddToListClick}
        trackAddToNewListClick={this.trackAddToNewListClick}
      />
    );
  }
}

TransactPanelContainer.propTypes = {
  bib: bibShape.isRequired,
  bibItems: ImmutablePropTypes.listOf(bibItemShape).isRequired,
  isLoaded: PropTypes.bool.isRequired,
  realtimeAvailability: availabilityShape,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired,
  digitalFormats: ImmutablePropTypes.list,
  availabilityError: PropTypes.string,
  currentLibrary: libraryShape.isRequired,
  holdings: ImmutablePropTypes.listOf(holdingShape).isRequired,
  subscriptions: ImmutablePropTypes.listOf(subscriptionShape).isRequired,
  bibsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentUser: userIdentityShape,
  trackAnalyticsEvent: PropTypes.func.isRequired,
  trackHoldingsConsumeAccess: PropTypes.func,
  trackHoldingsView: PropTypes.func
};

const mapStateToProps = state => {
  const catalogBib = selectCatalogBib(state);
  const metadataId = catalogBib.get('metadataId');
  const bib = selectBibEntities(state).get(metadataId);
  const isAvailabilityLoaded = state.getIn(['bibs', 'availability', 'isLoaded'], false);
  const isTransactionsLoaded = state.getIn(['bibs', 'userTransactions', 'isLoaded'], false);

  return {
    bib,
    catalogBib,
    bibItems: selectBibItemsForCurrentBib(state),
    isLoaded: isAvailabilityLoaded && isTransactionsLoaded,
    realtimeAvailability: selectAvailabilityEntities(state).get(metadataId),
    digitalFormats: selectAllDigitalFormats(state).get(metadataId),
    preferredBranches: selectActivePreferredBranches(state),
    availabilityError: selectAvailabilityError(state),
    currentLibrary: selectCurrentLibrary(state),
    holdings: selectHoldingsForCurrentBib(state),
    subscriptions: selectSubscriptionsForCurrentBib(state),
    currentAccount: selectCurrentAccount(state),
    currentUser: selectCurrentUser(state)
  };
};

const mapDispatchToProps = dispatch => ({
  bibsActions: bindActionCreators(BibsActions, dispatch),
  trackAnalyticsEvent: bindActionCreators(trackAnalyticsEvent, dispatch),
  trackHoldingsConsumeAccess: bindActionCreators(trackHoldingsConsumeAccess, dispatch),
  trackHoldingsView: bindActionCreators(trackHoldingsView, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(TransactPanelContainer));
